/**
 * The panel's working icon set — everything that is not a navigation destination.
 *
 * Same grid, same weight and the same `currentColor` discipline as {@link NavIcon}: a 24-unit box,
 * a 1.5 stroke, no fills and no colour of its own. Two files rather than one because the sidebar's
 * names are the routes' names, typed by the navigation data; these are named for what they mean on
 * a card, a toolbar or a row.
 *
 * All decorative by default. Anything that stands alone as a control carries its own label on the
 * button, which is where an accessible name belongs.
 */
export type IconName =
  | 'revenue'
  | 'cart'
  | 'today'
  | 'box'
  | 'people'
  | 'wallet'
  | 'clock'
  | 'lowStock'
  | 'star'
  | 'mail'
  | 'truck'
  | 'search'
  | 'filter'
  | 'sort'
  | 'plus'
  | 'edit'
  | 'trash'
  | 'more'
  | 'check'
  | 'chevronDown'
  | 'chevronEnd'
  | 'image'
  | 'inbox'
  | 'tag'
  | 'link'
  | 'copy'
  | 'download'
  | 'sheet'
  | 'alert'
  | 'page'
  | 'signOut'
  | 'external'
  | 'calendar'
  | 'chevronStart'
  | 'close'
  | 'back'
  | 'arrowUp'
  | 'arrowDown'
  | 'bold'
  | 'italic'
  | 'underline'
  | 'listBullet'
  | 'listNumbered'
  | 'quote'
  | 'unlink'
  | 'undo'
  | 'redo'
  | 'home'
  | 'book'
  | 'cup'
  | 'briefcase'
  | 'shield'
  | 'eye'
  | 'eyeOff'
  | 'filePdf'
  | 'fileExcel'
  | 'chart'
  | 'target'
  | 'stack'
  | 'chat'
  | 'warning'
  | 'refund';

/**
 * @param className  a hook for the sheet that positions the glyph — `.select__caret` rotates on
 *   open, `.datepicker__nav` glyphs flip in Latin. The reference's own icons take one; ours has
 *   to as well or those rules have nothing to bite on.
 * @param strokeWidth  a caret at 16px wants more weight than a 24px card glyph: the same 1.5
 *   stroke that reads as drawn at full size reads as a hairline in a control.
 */
export function Icon({
  name,
  size = 18,
  className,
  strokeWidth = 1.5,
}: {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {GLYPHS[name]}
    </svg>
  );
}

const GLYPHS: Record<IconName, React.ReactNode> = {
  /** A trend line over its axis — money coming in, not a currency symbol nobody can read at 18px. */
  revenue: (
    <>
      <path d="M4 19.2h16" opacity="0.5" />
      <path d="M5 15.4l4.4-4.6 3.4 2.9 5.6-6.3" />
      <path d="M14.2 7.4h4.2v4.1" />
    </>
  ),
  cart: (
    <>
      <path d="M3.4 4.6h2.2l2.3 9.6h8.7l2.1-7.1H6.6" />
      <circle cx="9.4" cy="18.4" r="1.4" />
      <circle cx="16.4" cy="18.4" r="1.4" />
    </>
  ),
  /** A day: the calendar with today marked. */
  today: (
    <>
      <rect x="3.8" y="5" width="16.4" height="15" rx="2.4" />
      <path d="M3.8 9.6h16.4M8.4 3.4v3.2M15.6 3.4v3.2" />
      <circle cx="12" cy="14.6" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  box: (
    <>
      <path d="M12 3.4l7.5 4v9.2l-7.5 4-7.5-4V7.4l7.5-4z" />
      <path d="M4.5 7.4l7.5 4 7.5-4M12 11.4v9.2" opacity="0.55" />
    </>
  ),
  people: (
    <>
      <circle cx="9.5" cy="8.6" r="3.3" />
      <path d="M3.6 19.6a5.9 5.9 0 0111.8 0" />
      <path d="M15.8 6.3a3.3 3.3 0 010 4.6M17.2 14.8a5.6 5.6 0 013.2 4.8" opacity="0.55" />
    </>
  ),
  /** Money not yet collected. */
  wallet: (
    <>
      <path d="M4 8.2A2.2 2.2 0 016.2 6h11.6A2.2 2.2 0 0120 8.2v8.6a2.2 2.2 0 01-2.2 2.2H6.2A2.2 2.2 0 014 16.8V8.2z" />
      <path d="M20 11.2h-3.4a1.8 1.8 0 000 3.6H20" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 7.4V12l3 1.9" />
    </>
  ),
  /** A shelf running out: bars stepping down to nothing. */
  lowStock: (
    <>
      <path d="M4 19.4h16" opacity="0.5" />
      <path d="M6.4 16.2V9.4M11.4 16.2v-3.4M16.4 16.2v-1" />
      <circle cx="16.4" cy="6.2" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  star: <path d="M12 4l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8L12 4z" />,
  mail: (
    <>
      <rect x="3.6" y="5.4" width="16.8" height="13.2" rx="2.4" />
      <path d="M4.4 8l6.4 4.4a2.1 2.1 0 002.4 0L19.6 8" />
    </>
  ),
  truck: (
    <>
      <path d="M3.4 7.4a1.6 1.6 0 011.6-1.6h7.6v9.6H3.4V7.4z" />
      <path d="M12.6 9.4h3.6l3.4 3.2v2.8h-7V9.4z" />
      <circle cx="7.4" cy="17.4" r="1.6" />
      <circle cx="16.6" cy="17.4" r="1.6" />
    </>
  ),
  search: (
    <>
      <circle cx="10.8" cy="10.8" r="6.2" />
      <path d="M15.4 15.4L20 20" />
    </>
  ),
  /** A funnel. Three bars would be a menu. */
  filter: <path d="M4.4 5.6h15.2l-6 7v6.2l-3.2-1.8v-4.4l-6-7z" />,
  sort: (
    <>
      <path d="M7.4 4.8v14M4.4 15.8l3 3 3-3" />
      <path d="M16.6 19.2V5.2M13.6 8.2l3-3 3 3" opacity="0.55" />
    </>
  ),
  plus: <path d="M12 5.4v13.2M5.4 12h13.2" />,
  edit: (
    <>
      <path d="M4.6 19.4h4l9.1-9.1a2 2 0 000-2.8l-1.2-1.2a2 2 0 00-2.8 0l-9.1 9.1v4z" />
      <path d="M13.4 6.6l4 4" opacity="0.55" />
    </>
  ),
  trash: (
    <>
      <path d="M4.8 6.8h14.4M9.4 6.8V5.2a1.4 1.4 0 011.4-1.4h2.4a1.4 1.4 0 011.4 1.4v1.6" />
      <path d="M6.6 6.8l.8 12.1a1.6 1.6 0 001.6 1.5h6a1.6 1.6 0 001.6-1.5l.8-12.1" />
    </>
  ),
  more: (
    <>
      <circle cx="12" cy="5.6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="18.4" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  check: <path d="M5 12.6l4.6 4.4L19 7" />,
  chevronDown: <path d="M6.4 9.4l5.6 5.4 5.6-5.4" />,
  /* Points the way the language reads; the caller flips it in RTL by transform. */
  chevronEnd: <path d="M9.4 5.6l6 6.4-6 6.4" />,
  chevronStart: <path d="M14.6 5.6l-6 6.4 6 6.4" />,
  /* `today` with its dot removed: a month, rather than a particular day in one. */
  calendar: (
    <>
      <rect x="3.8" y="5" width="16.4" height="15" rx="2.4" />
      <path d="M3.8 9.6h16.4M8.4 3.4v3.2M15.6 3.4v3.2" />
    </>
  ),
  close: <path d="M6.4 6.4l11.2 11.2M17.6 6.4L6.4 17.6" />,
  /* An arrow back along the reading direction — flipped in Latin by `.ltr-flip`. */
  back: <path d="M4.6 12h14.8M11 5.6L4.6 12l6.4 6.4" />,
  image: (
    <>
      <rect x="3.8" y="5" width="16.4" height="14" rx="2.4" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="M4.6 17l4.6-4.4 3.4 3 2.8-2.4 4 3.8" />
    </>
  ),
  inbox: (
    <>
      <path d="M3.8 13.4l2.5-7a2 2 0 011.9-1.4h7.6a2 2 0 011.9 1.4l2.5 7v3.4a2.2 2.2 0 01-2.2 2.2H6a2.2 2.2 0 01-2.2-2.2v-3.4z" />
      <path d="M3.8 13.4h4.4a1 1 0 011 .8 2.9 2.9 0 005.6 0 1 1 0 011-.8h4.4" />
    </>
  ),
  tag: (
    <>
      <path d="M11.4 4.2H19a.8.8 0 01.8.8v7.6a2 2 0 01-.6 1.4l-5.4 5.4a1.6 1.6 0 01-2.3 0l-6.7-6.7a1.6 1.6 0 010-2.3L10.2 5a2 2 0 011.2-.8z" />
      <circle cx="15.4" cy="8.6" r="1.3" />
    </>
  ),
  link: (
    <>
      <path d="M10.4 13.6a3.6 3.6 0 005.4.4l2.4-2.4a3.6 3.6 0 00-5.1-5.1l-1.3 1.3" />
      <path d="M13.6 10.4a3.6 3.6 0 00-5.4-.4l-2.4 2.4a3.6 3.6 0 005.1 5.1l1.3-1.3" />
    </>
  ),
  copy: (
    <>
      <rect x="8.6" y="8.6" width="11" height="11" rx="2.2" />
      <path d="M15.4 5.4a2.2 2.2 0 00-2.2-2H6.6a2.2 2.2 0 00-2.2 2.2v6.6a2.2 2.2 0 002 2.2" opacity="0.55" />
    </>
  ),
  /** Into the tray: a file coming down to this computer. Vertical, so it never mirrors. */
  download: (
    <>
      <path d="M12 4.6v10M7.8 10.6l4.2 4.2 4.2-4.2" />
      <path d="M4.8 15.6v2.2a1.8 1.8 0 001.8 1.8h10.8a1.8 1.8 0 001.8-1.8v-2.2" />
    </>
  ),
  /* The dashboard's own glyphs: its heading, its sections and the figures they hold. */
  chart: (
    <>
      <path d="M4.6 19.6h14.8" opacity="0.5" />
      <rect x="6.2" y="11.2" width="3" height="6" rx="1" />
      <rect x="10.5" y="6.4" width="3" height="10.8" rx="1" />
      <rect x="14.8" y="9" width="3" height="8.2" rx="1" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="12" cy="12" r="4.6" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  stack: (
    <>
      <path d="M12 4.4l8 4-8 4-8-4 8-4z" />
      <path d="M4 12.2l8 4 8-4" />
      <path d="M4 16l8 4 8-4" opacity="0.6" />
    </>
  ),
  chat: (
    <>
      <path d="M20 11.4c0 3.6-3.6 6.4-8 6.4-1 0-2-.1-2.9-.4L4.6 19l1.2-3.4C4.7 14.5 4 13 4 11.4 4 7.8 7.6 5 12 5s8 2.8 8 6.4z" />
      <circle cx="8.8" cy="11.4" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="11.4" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15.2" cy="11.4" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
  warning: (
    <>
      <path d="M10.3 4.9a2 2 0 013.4 0l6.6 11.5a2 2 0 01-1.7 3H5.4a2 2 0 01-1.7-3l6.6-11.5z" />
      <path d="M12 9.6v4" />
      <circle cx="12" cy="16.4" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  refund: (
    <>
      <path d="M5.4 12a6.6 6.6 0 102-4.7" />
      <path d="M4.9 4.4v3.4h3.4" />
    </>
  ),
  /*
   * The two export formats: the top of a page with its corner folded, and the format's letters
   * under it — the shape a file manager draws, so the button is recognised before it is read.
   */
  filePdf: (
    <>
      <path d="M5 10.5V4.8A1.8 1.8 0 016.8 3h7.4L19 7.8v2.7" />
      <path d="M14 3v3.4a1.4 1.4 0 001.4 1.4H19" />
      <path d="M4 20.5v-7h2.1a2.1 2.1 0 010 4.2H4" />
      <path d="M10 13.5v7h1.4a2.6 2.6 0 002.6-2.6v-1.8a2.6 2.6 0 00-2.6-2.6z" />
      <path d="M16.2 20.5v-7H20M16.2 17h3" />
    </>
  ),
  fileExcel: (
    <>
      <path d="M5 10.5V4.8A1.8 1.8 0 016.8 3h7.4L19 7.8v2.7" />
      <path d="M14 3v3.4a1.4 1.4 0 001.4 1.4H19" />
      <path d="M4 13.5l4 7M8 13.5l-4 7" />
      <path d="M10 13.5v7h3.6" />
      <path d="M19.6 13.5h-2.4a1.75 1.75 0 000 3.5h1.2a1.75 1.75 0 010 3.5h-2.6" />
    </>
  ),
  /** A grid of cells with a heading row: a spreadsheet, beside `page` for a document. */
  sheet: (
    <>
      <rect x="4.6" y="5" width="14.8" height="14" rx="2" />
      <path d="M4.6 9.6h14.8M4.6 14.2h14.8M10 9.6V19" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 7.8v4.8" />
      <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  /**
   * Leaves the page: a box with an arrow going out of its corner. Drawn pointing up-and-right,
   * and mirrored by the caller in Arabic so "away" is always toward the reading END.
   */
  external: (
    <>
      <path d="M13.6 4.6H19.4V10.4" />
      <path d="M19.4 4.6L11.4 12.6" />
      <path d="M18 14.2v3.9a2 2 0 01-2 2H6.5a2 2 0 01-2-2V8.6a2 2 0 012-2h3.9" />
    </>
  ),
  /** A door with an arrow leaving through it. Mirrors itself with the reading direction. */
  signOut: (
    <>
      <path d="M14.5 4.5H17A2.5 2.5 0 0119.5 7v10a2.5 2.5 0 01-2.5 2.5h-2.5" />
      <path d="M10.5 15.5L14 12l-3.5-3.5M14 12H4.5" />
    </>
  ),
  /* Moving a section one place up or down its page. Vertical, so they never mirror. */
  arrowUp: <path d="M12 18.6V5.4M6.6 10.8L12 5.4l5.4 5.4" />,
  arrowDown: <path d="M12 5.4v13.2M6.6 13.2l5.4 5.4 5.4-5.4" />,

  /*
   * The rich-text toolbar. Drawn rather than typed: "B", "I" and "H" set as letters took the
   * panel's own face, which in Arabic is not the face those letters are recognised in.
   */
  bold: <path d="M7.4 5h5.2a3.5 3.5 0 010 7H7.4zM7.4 12h6.1a3.5 3.5 0 010 7H7.4z" />,
  italic: <path d="M10.4 5h7.2M6.4 19h7.2M14 5l-4 14" />,
  underline: <path d="M7.2 5v6.2a4.8 4.8 0 009.6 0V5M5.6 19.4h12.8" />,
  listBullet: (
    <>
      <path d="M9.6 7h9.8M9.6 12h9.8M9.6 17h9.8" />
      <circle cx="5.4" cy="7" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="5.4" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="5.4" cy="17" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  listNumbered: (
    <>
      <path d="M10.6 7h8.8M10.6 12h8.8M10.6 17h8.8" />
      <path d="M4.8 5.6l1.4-.8v4" strokeWidth="1.3" />
      <path d="M4.7 11.2c.3-.6.9-.9 1.5-.8.7.1 1 .6.9 1.2-.1.7-2.4 1.6-2.4 2.4h2.5" strokeWidth="1.3" />
    </>
  ),
  /* A quoted block: the rule down its edge and the lines it sets apart. */
  quote: <path d="M5.4 5.6v12.8M9.6 7.6h9M9.6 12h9M9.6 16.4h6" />,
  unlink: (
    <>
      <path d="M13.6 10.4a3.6 3.6 0 00-.9-.6M15.8 14l2.4-2.4a3.6 3.6 0 00-5.1-5.1l-1.3 1.3" />
      <path d="M8.2 10l-2.4 2.4a3.6 3.6 0 005.1 5.1l1.3-1.3" />
      <path d="M4.8 4.8l14.4 14.4" />
    </>
  ),
  /* Back and forward through edits. Mirrored by the caller in Arabic, like `back`. */
  undo: <path d="M9.4 6.4L5 10.8l4.4 4.4M5 10.8h9.4a4.6 4.6 0 010 9.2H12" />,
  redo: <path d="M14.6 6.4l4.4 4.4-4.4 4.4M19 10.8H9.6a4.6 4.6 0 000 9.2H12" />,

  /* The shop's pages, as the content studio lists them. */
  home: <path d="M4.4 11.2L12 4.6l7.6 6.6M6.6 9.4v9.4a1 1 0 001 1h8.8a1 1 0 001-1V9.4M10 19.8v-5h4v5" />,
  book: <path d="M12 6.6C10.2 5.3 7.7 4.8 4.6 5v12.6c3.1-.2 5.6.3 7.4 1.6 1.8-1.3 4.3-1.8 7.4-1.6V5c-3.1-.2-5.6.3-7.4 1.6zM12 6.6v12.6" />,
  cup: (
    <>
      <path d="M5 9.4h11v4.2a4.6 4.6 0 01-4.6 4.6h-1.8A4.6 4.6 0 015 13.6V9.4z" />
      <path d="M16 10.6h1.3a2.3 2.3 0 010 4.6H16M8.4 4.2v2.6M11.6 4.2v2.6" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3.8" y="7.4" width="16.4" height="11.8" rx="2.2" />
      <path d="M9 7.4V6a1.6 1.6 0 011.6-1.6h2.8A1.6 1.6 0 0115 6v1.4M3.8 12.6h16.4" />
    </>
  ),
  shield: (
    <>
      <path d="M12 4l7 2.6v5.2c0 4.2-3 7-7 8.2-4-1.2-7-4-7-8.2V6.6L12 4z" />
      <path d="M9.2 12.2l2 2 3.6-3.8" />
    </>
  ),

  /* Showing and hiding what a password field holds. */
  eye: (
    <>
      <path d="M2.8 12s3.4-6.4 9.2-6.4 9.2 6.4 9.2 6.4-3.4 6.4-9.2 6.4S2.8 12 2.8 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M9.9 6a9.6 9.6 0 012.1-.4c5.8 0 9.2 6.4 9.2 6.4a16 16 0 01-2.4 3.2M6.2 7.6A15.4 15.4 0 002.8 12s3.4 6.4 9.2 6.4a9 9 0 004.4-1.1" />
      <path d="M10 10a2.8 2.8 0 004 4M4.6 4.6l14.8 14.8" />
    </>
  ),

  /** A page with a heading and copy under it, which is what a content section is. */
  page: (
    <>
      <rect x="4.5" y="3.5" width="15" height="17" rx="2.2" />
      <path d="M8 8.4h8" />
      <path d="M8 12.2h8M8 15.9h5" opacity="0.55" />
    </>
  ),
};
